import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'dynamicTreeMap',
  name_zh: '动态矩形树图',
  name_en: 'Dynamic TreeMap',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    data: amount => amount > 1,
    x: amount => amount > 1,
    y: amount => amount > 1,
    group: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    group: {
      itemType: [DEFINE.DATE],
      category: DEFINE.SINGLE
    },
    color: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE,
      optional: true
    }
  }
};

/** 暂不支持legend的点击功能 */
export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    group: string;
    color?: string;
  };
}

export const layer = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  withTimeline: true,
  visInfo,
  render(api, spec) {
    const { data: dataArray, util, hook } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, group: groupBy, color }
    } = spec;
    const data = dataArray[dataIndex].values;
    const dataMetaInfo = hook.parseDataMetaInfo(data);
    const { years } = util.dataEncoding.encoding(data, {
      years: {
        field: groupBy,
        transforms: [{ type: 'deDuplication' }, { type: 'sort', order: 'ascending' }]
      }
    });

    // * ------------------------- data
    const getSeriesData = (data, index) => {
      const tempDatas = data.filter(item => item[groupBy] === years[index]);
      return tempDatas.map((tempData, idx) => ({
        name: tempData[x],
        value: tempData[y],
        color: color ? tempData[color] : undefined
      }));
    };
    // * ------------------------- chart
    const options = years.map((_, index) => ({
      tooltip: {
        trigger: 'item',
        formatter: params => {
          const series = Array.isArray(params) ? params : [params];
          const items = [];

          series.forEach(item => {
            const result = hook.formatDataNumberValue(item.value, {
              type: 'table',
              unit: dataMetaInfo[item.seriesName]?.unit
            });

            items.push({
              type: util.const.SERIES_2_LEGEND_TYPE_MAP[item.seriesType],
              color: item.color,
              name: item.name,
              value: result.value + result.unit
            });
          });

          return hook.buildTooltip({
            config: hook.buildStandardChartPresetOption(api).tooltip,
            title: '',
            items
          });
        }
      },
      xAxis: [],
      yAxis: [],
      series: [
        {
          type: 'treemap',
          roam: false,
          nodeClick: false,
          width: '100%',
          height: '100%',
          label: {
            formatter: params_ => {
              const result = hook.formatDataNumberValue(params_.data.value, {
                type: 'table',
                unit: dataMetaInfo[params_.seriesName]?.unit
              });

              return `${params_.data.name}\n\n${result.value + result.unit}`;
            }
          },
          // 矩形图元文本高度超过矩形块高度则直接隐藏文本
          labelLayout: params => {
            if (params.labelRect.height > params.rect.height) {
              return {
                fontSize: 0
              };
            }
          },
          breadcrumb: {
            show: false
          },
          itemStyle: {
            gapWidth: 1
          },
          data: getSeriesData(data, index)
        }
      ]
    }));

    return renderApi => {
      const renderParams = { option: renderApi.mergeChartOptionProxy(options[options.length - 1]) };
      const timelineIns = renderApi.renderTimelineProxy(years as string[]);
      renderApi.renderLegendProxy(renderParams.option.legend.data);
      const chartIns = renderApi.renderChartProxy(renderParams, { theme: 'pc-app-light' });
      timelineIns.on('change', e => {
        renderParams.option = renderApi.mergeChartOptionProxy(options[e.index]);
        renderApi.optionCallbackProxy(renderParams.option);
        api.hook.beforeRenderStandardChart(api, renderParams);

        chartIns.play(renderParams);

        renderApi.cacheRenderOptionProxy(renderParams.option);
      });
    };
  }
});
